'use client'

import type {ComponentProps, ReactNode} from 'react'
import React from 'react'
import {Link, useLocation} from 'react-router-dom'
import {useApp} from 'src/layouts/main-layout/contexts/AppContext'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/layouts/main-layout/header/components/route-breadcrumb/breadcrumb'
import {tv, type VariantProps} from 'tailwind-variants'

type RouteLabel = string | ((ctx: {segment: string; href: string; params: Record<string, string | string[] | undefined>}) => string)

export type BreadcrumbMap = Record<string, RouteLabel>

export interface BreadcrumbItem {
  href: string
  label: string
  isCurrent: boolean
}

const breadcrumbVariants = tv({
  base: ['flex items-center gap-2 text-sm'],
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export interface RouteBreadcrumbProps extends ComponentProps<'nav'>, VariantProps<typeof breadcrumbVariants> {
  map?: BreadcrumbMap
  params?: Record<string, string | string[] | undefined>
  overrides?: Partial<Record<string, string>> // key = href
  append?: Array<{label: string; href?: string}>
  formatSegmentLabel?: (segment: string) => string
  separator?: ReactNode
}

function stripQueryAndHash(pathname: string) {
  const q = pathname.indexOf('?')
  const h = pathname.indexOf('#')
  const cut = Math.min(q === -1 ? pathname.length : q, h === -1 ? pathname.length : h)
  return pathname.slice(0, cut)
}

function cleanSegments(pathname: string) {
  return stripQueryAndHash(pathname).split('/').filter(Boolean)
}

function matchRouteLabel(map: BreadcrumbMap, href: string) {
  if (map[href]) return map[href]

  for (const key of Object.keys(map)) {
    if (!key.includes('/:')) continue

    const keyParts = key.split('/').filter(Boolean)
    const hrefParts = href.split('/').filter(Boolean)
    if (keyParts.length !== hrefParts.length) continue

    let ok = true
    for (let i = 0; i < keyParts.length; i++) {
      const kp = keyParts[i]
      const hp = hrefParts[i]
      if (kp.startsWith(':')) continue
      if (kp !== hp) {
        ok = false
        break
      }
    }

    if (ok) return map[key]
  }

  return null
}

function buildItems(args: {
  pathname: string
  map: BreadcrumbMap
  params: Record<string, string | string[] | undefined>
  overrides?: Partial<Record<string, string>>
}): BreadcrumbItem[] {
  const {pathname, map, params, overrides} = args
  const segments = cleanSegments(pathname)

  const items: BreadcrumbItem[] = []
  let acc = ''

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    acc += `/${segment}`

    const isCurrent = i === segments.length - 1

    const routeLabel = matchRouteLabel(map, acc)
    let label: string

    if (overrides?.[acc]) {
      label = overrides[acc] as string
    } else if (typeof routeLabel === 'function') {
      label = routeLabel({segment, href: acc, params})
    } else if (typeof routeLabel === 'string') {
      label = routeLabel
    } else {
      label = ''
    }

    items.push({href: acc, label, isCurrent})
  }

  return items.filter((item) => item.label.length > 0)
}

export function RouteBreadcrumb({map = {}, params = {}, overrides, append}: RouteBreadcrumbProps) {
  const {pathname} = useLocation()
  const {subMenuName} = useApp()
  const items = buildItems({
    pathname,
    map,
    params,
    overrides,
  })

  const withAppend: Array<BreadcrumbItem | {label: string; href?: string; isCurrent?: boolean}> = [...items]

  if (append?.length) {
    for (const item of append) {
      withAppend.push({
        label: item.label,
        href: item.href,
        isCurrent: true,
      })
    }

    for (let i = 0; i < withAppend.length; i++) {
      const it = withAppend[i]
      if ('isCurrent' in it) it.isCurrent = i === withAppend.length - 1
    }
  }

  if (withAppend.length === 0) return null

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {withAppend.map((item, idx) => {
          const isLast = idx === withAppend.length - 1
          const key = 'href' in item && item.href ? item.href : `${item.label}-${idx}`

          return (
            <React.Fragment key={key}>
              <BreadcrumbItem>
                {'href' in item && item.href && !('isCurrent' in item && item.isCurrent) ? (
                  <>
                    <BreadcrumbLink asChild>
                      <Link style={{textDecoration: 'none'}} to={item.href}>
                        {item.label}
                      </Link>
                    </BreadcrumbLink>
                    {'>'}
                    <BreadcrumbPage>{subMenuName}</BreadcrumbPage>
                  </>
                ) : (
                  <BreadcrumbPage>{item.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {!isLast ? <BreadcrumbSeparator /> : null}
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
